import { FC } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock } from "lucide-react";
import { PaymentTiming, PromotionResource } from "@/interfaces";
import { cn } from "@/lib/utils";
import { formatCurrency, formatDate } from "@/utils/currency";
import RadioDot from "./payment/RadioDot";
import { planMissesPromotionDeadline } from "@/utils/promotion-deadline";

interface Props {
  promotion?: PromotionResource | null;
  planStartDate?: string | null;
  /** A plan of more than one instalment earns the discount on its FIRST payment. */
  installments?: number;
  value: PaymentTiming;
  onChange: (timing: PaymentTiming) => void;
  disabled?: boolean;
}

const Option: FC<{
  checked: boolean;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ checked, title, subtitle, onClick, disabled }) => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "flex w-full min-h-16 gap-2 px-3 py-3 border rounded-xl text-left transition-all",
      checked ? "border-primary bg-muted" : "border-border",
      disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
    )}
  >
    <div className="flex items-start gap-3">
      <RadioDot checked={checked} />
      <div className="min-w-0 flex flex-col items-start">
        <div className="text-sm mb-1 font-semibold text-foreground leading-none">
          {title}
        </div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
    </div>
  </button>
);

/**
 * A promotion whose stop_at is a real payment deadline cannot be honoured by a
 * payment plan that only charges months later. Rather than advertising a
 * discount the schedule contradicts, let the customer say when they want to pay
 * — and be explicit that keeping the plan's own date gives the discount up.
 */
const PaymentTimingChoice: FC<Props> = ({
  promotion,
  planStartDate,
  installments = 1,
  value,
  onChange,
  disabled,
}) => {
  const { t, i18n } = useTranslation();

  const deadline = promotion?.attributes?.stop_at;

  // Nothing to decide when the plan's own schedule already earns the discount.
  if (!planMissesPromotionDeadline(promotion, planStartDate) || !deadline) {
    return null;
  }

  const isInstalmentPlan = installments > 1;

  const savings = promotion.attributes.savings
    ? formatCurrency(Math.abs(promotion.attributes.savings), i18n.language)
    : null;

  const keepsDiscount = savings
    ? t("fe.pages.flow.steps.payment.timingKeepsDiscount", {
        savings,
        defaultValue: "Discount applied — you save {{savings}}",
      })
    : t("fe.pages.flow.steps.payment.timingKeepsDiscountPlain", {
        defaultValue: "Discount applied",
      });

  return (
    <section className="mt-6 rounded-2xl border border-muted-foreground/20 bg-muted/30 p-5">
      <div className="flex items-center gap-2 mb-2">
        <CalendarClock size={18} className="text-primary" />
        <h3 className="text-base font-semibold text-foreground">
          {t("fe.pages.flow.steps.payment.timingTitle", {
            defaultValue: "When would you like to pay?",
          })}
        </h3>
      </div>

      <p className="text-sm leading-5 text-muted-foreground mb-5">
        {isInstalmentPlan
          ? t("fe.pages.flow.steps.payment.timingSubtitleInstalments", {
              date: formatDate(deadline, i18n.language),
              defaultValue:
                "This discount is only earned if your first payment is made by {{date}}. Your other payments keep the same gaps and move with it.",
            })
          : t("fe.pages.flow.steps.payment.timingSubtitle", {
              date: formatDate(deadline, i18n.language),
              defaultValue:
                "This discount is only earned if your balance is paid by {{date}}.",
            })}
      </p>

      <div role="radiogroup" className="flex flex-col gap-3">
        <Option
          checked={value === "immediately"}
          disabled={disabled}
          onClick={() => onChange("immediately")}
          title={
            isInstalmentPlan
              ? t("fe.pages.flow.steps.payment.timingNowInstalments", {
                  defaultValue: "Pay the first instalment now",
                })
              : t("fe.pages.flow.steps.payment.timingNow", {
                  defaultValue: "Pay now",
                })
          }
          subtitle={keepsDiscount}
        />

        <Option
          checked={value === "by_promotion_deadline"}
          disabled={disabled}
          onClick={() => onChange("by_promotion_deadline")}
          title={
            isInstalmentPlan
              ? t("fe.pages.flow.steps.payment.timingByDeadlineInstalments", {
                  date: formatDate(deadline, i18n.language),
                  defaultValue: "Make your first payment by {{date}}",
                })
              : t("fe.pages.flow.steps.payment.timingByDeadline", {
                  date: formatDate(deadline, i18n.language),
                  defaultValue: "Pay by {{date}}",
                })
          }
          subtitle={keepsDiscount}
        />

        <Option
          checked={value === "plan_schedule"}
          disabled={disabled}
          onClick={() => onChange("plan_schedule")}
          title={
            planStartDate
              ? isInstalmentPlan
                ? t("fe.pages.flow.steps.payment.timingPlanDatedInstalments", {
                    date: formatDate(planStartDate, i18n.language),
                    defaultValue: "Start paying on {{date}} as scheduled",
                  })
                : t("fe.pages.flow.steps.payment.timingPlanDated", {
                    date: formatDate(planStartDate, i18n.language),
                    defaultValue: "Pay on {{date}} as scheduled",
                  })
              : t("fe.pages.flow.steps.payment.timingPlan", {
                  defaultValue: "Pay on the normal schedule",
                })
          }
          subtitle={t("fe.pages.flow.steps.payment.timingForfeits", {
            defaultValue: "No discount — the full amount is charged",
          })}
        />
      </div>
    </section>
  );
};

export default PaymentTimingChoice;
